import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Modal,
  Form,
  InputNumber,
  Switch,
  message,
  Popconfirm,
  Radio,
  Checkbox,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
} from '@ant-design/icons';

const { Option } = Select;
const { TextArea } = Input;

interface DataSource {
  id: number;
  name: string;
  type: 'web' | 'api' | 'database' | 'rss' | 'social';
  sourceUrl: string;
  dataFields: string[]; // 需要爬取的数据字段
  crawlFrequency: 'realtime' | 'hourly' | 'daily' | 'weekly';
  crawlSchedule: string; // 爬取时间计划
  enabled: boolean;
  description: string;
  createTime: string;
  updateTime: string;
}

// Demo数据
const initialData: DataSource[] = [
  {
    id: 1,
    name: '足球赛事数据源',
    type: 'api',
    sourceUrl: 'https://api.football.com/matches',
    dataFields: ['比赛时间', '主队', '客队', '比分', '赔率', '分析'],
    crawlFrequency: 'hourly',
    crawlSchedule: '每小时整点',
    enabled: true,
    description: '爬取足球赛事数据，用于生成对战分析报告',
    createTime: '2025-12-01 10:00:00',
    updateTime: '2025-12-01 10:00:00',
  },
  {
    id: 2,
    name: '美女图片数据源',
    type: 'web',
    sourceUrl: 'https://beauty-images.com/latest',
    dataFields: ['图片URL', '标题', '标签', '描述', '作者'],
    crawlFrequency: 'daily',
    crawlSchedule: '每日 08:00, 14:00, 20:00',
    enabled: true,
    description: '爬取美女图片，用于生成直播预告图',
    createTime: '2025-12-01 10:00:00',
    updateTime: '2025-12-01 10:00:00',
  },
  {
    id: 3,
    name: '游戏攻略数据源',
    type: 'web',
    sourceUrl: 'https://game-guide.com/articles',
    dataFields: ['标题', '内容', '游戏类型', '作者', '发布时间'],
    crawlFrequency: 'daily',
    crawlSchedule: '每日 09:00',
    enabled: true,
    description: '爬取游戏攻略文章，用于生成攻略内容',
    createTime: '2025-12-01 10:00:00',
    updateTime: '2025-12-01 10:00:00',
  },
  {
    id: 4,
    name: '新闻资讯RSS',
    type: 'rss',
    sourceUrl: 'https://news.com/rss',
    dataFields: ['标题', '内容', '发布时间', '分类', '来源'],
    crawlFrequency: 'hourly',
    crawlSchedule: '每小时整点',
    enabled: true,
    description: '爬取新闻资讯，用于生成每日新闻摘要',
    createTime: '2025-12-01 10:00:00',
    updateTime: '2025-12-01 10:00:00',
  },
  {
    id: 5,
    name: '社交媒体热点',
    type: 'social',
    sourceUrl: 'https://social-trends.com/api',
    dataFields: ['话题', '热度', '相关视频', '相关图片', '评论'],
    crawlFrequency: 'realtime',
    crawlSchedule: '实时监控',
    enabled: true,
    description: '实时监控社交媒体热点，用于生成热点内容',
    createTime: '2025-12-01 10:00:00',
    updateTime: '2025-12-01 10:00:00',
  },
  {
    id: 6,
    name: '视频素材库',
    type: 'database',
    sourceUrl: 'internal://video-library',
    dataFields: ['视频ID', '标题', '时长', '标签', '分类', 'URL'],
    crawlFrequency: 'daily',
    crawlSchedule: '每日 06:00',
    enabled: true,
    description: '从内部视频素材库获取视频数据',
    createTime: '2025-12-01 10:00:00',
    updateTime: '2025-12-01 10:00:00',
  },
  {
    id: 7,
    name: '体育赛事直播数据',
    type: 'api',
    sourceUrl: 'https://sports-live.com/api/streams',
    dataFields: ['赛事名称', '直播时间', '直播地址', '解说员', '观看人数'],
    crawlFrequency: 'realtime',
    crawlSchedule: '实时监控',
    enabled: true,
    description: '实时获取体育赛事直播数据，用于生成解说音频',
    createTime: '2025-12-01 10:00:00',
    updateTime: '2025-12-01 10:00:00',
  },
  {
    id: 8,
    name: '用户行为数据库',
    type: 'database',
    sourceUrl: 'internal://user-behavior',
    dataFields: ['用户ID', '行为类型', '时间', '内容ID', '互动数据'],
    crawlFrequency: 'hourly',
    crawlSchedule: '每小时整点',
    enabled: true,
    description: '从用户行为数据库获取数据，用于分析内容效果',
    createTime: '2025-12-01 10:00:00',
    updateTime: '2025-12-01 10:00:00',
  },
];

const DataSourceStrategy: React.FC = () => {
  const [data, setData] = useState<DataSource[]>(initialData);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<DataSource | null>(null);
  const [searchText, setSearchText] = useState('');
  const [form] = Form.useForm();

  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: DataSource) => {
    setEditingRecord(record);
    form.setFieldsValue({
      ...record,
      dataFields: record.dataFields,
    });
    setModalVisible(true);
  };

  const handleDelete = (id: number) => {
    setData(prev => prev.filter(item => item.id !== id));
    message.success('删除成功');
  };

  const handleSave = () => {
    form.validateFields().then(values => {
      if (editingRecord) {
        setData(prev =>
          prev.map(item =>
            item.id === editingRecord.id
              ? { ...item, ...values, updateTime: new Date().toLocaleString('zh-CN') }
              : item
          )
        );
        message.success('更新成功');
      } else {
        const newRecord: DataSource = {
          id: Date.now(),
          ...values,
          enabled: true,
          createTime: new Date().toLocaleString('zh-CN'),
          updateTime: new Date().toLocaleString('zh-CN'),
        };
        setData(prev => [...prev, newRecord]);
        message.success('新增成功');
      }
      setModalVisible(false);
      form.resetFields();
    });
  };

  const handleToggleEnabled = (id: number, enabled: boolean) => {
    setData(prev =>
      prev.map(item =>
        item.id === id ? { ...item, enabled: !enabled, updateTime: new Date().toLocaleString('zh-CN') } : item
      )
    );
    message.success(enabled ? '已禁用' : '已启用');
  };

  const filteredData = data.filter(item =>
    item.name.includes(searchText) ||
    item.sourceUrl.includes(searchText) ||
    item.description.includes(searchText)
  );

  const DATA_FIELD_OPTIONS = [
    '比赛时间', '主队', '客队', '比分', '赔率', '分析',
    '图片URL', '标题', '标签', '描述', '作者',
    '内容', '游戏类型', '发布时间',
    '话题', '热度', '相关视频', '相关图片', '评论',
    '视频ID', '时长', '分类',
    '赛事名称', '直播时间', '直播地址', '解说员', '观看人数',
    '用户ID', '行为类型', '时间', '内容ID', '互动数据',
  ];

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '数据源名称',
      dataIndex: 'name',
      key: 'name',
      width: 180,
    },
    {
      title: '数据源类型',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type: string) => {
        const map: Record<string, { text: string; color: string }> = {
          web: { text: '网页', color: 'blue' },
          api: { text: 'API', color: 'green' },
          database: { text: '数据库', color: 'orange' },
          rss: { text: 'RSS', color: 'purple' },
          social: { text: '社交媒体', color: 'red' },
        };
        const info = map[type] || { text: type, color: 'default' };
        return <Tag color={info.color}>{info.text}</Tag>;
      },
    },
    {
      title: '数据源地址',
      dataIndex: 'sourceUrl',
      key: 'sourceUrl',
      width: 250,
      ellipsis: true,
    },
    {
      title: '数据字段',
      dataIndex: 'dataFields',
      key: 'dataFields',
      width: 300,
      render: (fields: string[]) => (
        <Space wrap>
          {fields.map(field => (
            <Tag key={field}>{field}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: '爬取频率',
      dataIndex: 'crawlFrequency',
      key: 'crawlFrequency',
      width: 120,
      render: (freq: string) => {
        const map: Record<string, string> = {
          realtime: '实时',
          hourly: '每小时',
          daily: '每日',
          weekly: '每周',
        };
        return map[freq] || freq;
      },
    },
    {
      title: '爬取计划',
      dataIndex: 'crawlSchedule',
      key: 'crawlSchedule',
      width: 150,
    },
    {
      title: '状态',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 100,
      render: (enabled: boolean, record: DataSource) => (
        <Switch
          checked={enabled}
          onChange={() => handleToggleEnabled(record.id, enabled)}
        />
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right' as const,
      render: (_: any, record: DataSource) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Popconfirm
            title="确认删除？"
            onConfirm={() => handleDelete(record.id)}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div className="page-header">
        <h1>数据源策略配置</h1>
        <p>配置需要哪些数据，系统自动爬取。支持网页、API、数据库、RSS、社交媒体等多种数据源</p>
      </div>

      <Card>
        <Space style={{ marginBottom: 16 }}>
          <Input
            placeholder="搜索数据源名称、地址或描述"
            prefix={<SearchOutlined />}
            style={{ width: 300 }}
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            新增
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => message.info('刷新成功')}>
            刷新
          </Button>
        </Space>

        <Table
          rowKey="id"
          dataSource={filteredData}
          columns={columns}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 1500 }}
        />
      </Card>

      <Modal
        title={editingRecord ? '编辑数据源策略' : '新增数据源策略'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        onOk={handleSave}
        width={800}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="数据源名称"
            rules={[{ required: true, message: '请输入数据源名称' }]}
          >
            <Input placeholder="如：足球赛事数据源" />
          </Form.Item>
          <Form.Item
            name="type"
            label="数据源类型"
            rules={[{ required: true, message: '请选择数据源类型' }]}
          >
            <Radio.Group>
              <Radio value="web">网页</Radio>
              <Radio value="api">API</Radio>
              <Radio value="database">数据库</Radio>
              <Radio value="rss">RSS</Radio>
              <Radio value="social">社交媒体</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            name="sourceUrl"
            label="数据源地址"
            rules={[{ required: true, message: '请输入数据源地址' }]}
          >
            <Input placeholder="如：https://api.example.com/data" />
          </Form.Item>
          <Form.Item
            name="dataFields"
            label="需要爬取的数据字段"
            rules={[{ required: true, message: '请选择至少一个数据字段' }]}
          >
            <Checkbox.Group>
              {DATA_FIELD_OPTIONS.map(field => (
                <Checkbox key={field} value={field}>{field}</Checkbox>
              ))}
            </Checkbox.Group>
          </Form.Item>
          <Form.Item
            name="crawlFrequency"
            label="爬取频率"
            rules={[{ required: true, message: '请选择爬取频率' }]}
          >
            <Radio.Group>
              <Radio value="realtime">实时</Radio>
              <Radio value="hourly">每小时</Radio>
              <Radio value="daily">每日</Radio>
              <Radio value="weekly">每周</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            name="crawlSchedule"
            label="爬取计划"
            rules={[{ required: true, message: '请输入爬取计划' }]}
          >
            <Input placeholder="如：每小时整点 或 每日 08:00, 14:00, 20:00" />
          </Form.Item>
          <Form.Item
            name="description"
            label="描述"
            rules={[{ required: true, message: '请输入描述' }]}
          >
            <TextArea rows={3} placeholder="描述该数据源的用途" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DataSourceStrategy;

